import {
  useState,
  useRef,
  useEffect,
  type KeyboardEvent,
  type ClipboardEvent,
} from "react";
import { Button } from "@/components/ui/button/Button";
import { Badge } from "@/components/ui/badge/Badge";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPaperPlaneTop,
  faMicrophone,
  faPaperclip,
  faXmark,
  faFile,
} from "@awesome.me/kit-70c1f0055f/icons/classic/light";
import { faCircle } from "@awesome.me/kit-70c1f0055f/icons/classic/solid";
import styles from "./Chat.module.css";
import {
  insertPlainTextAtSelection,
  serializeContentEditableToPlainText,
} from "./contentEditableText";
import { useComposerVoiceInput } from "./useComposerVoiceInput";
import { UploadDialog } from "@/components/documents/UploadDialog";
import { uploadAgenticDocument } from "@/services/api/agentic";
import {
  uploadConversationDocument,
  uploadTaskDocument,
  type MessageAttachment,
} from "@/services/api/agentic-v2";
import type { Document as DocumentsDocument } from "@/services/api/documents/types";
import type { IngestAPIResponse } from "@/services/api/data-hub/types";
import { useAuth } from "@/context/AuthContext";
import type { AniPersonalityMode } from "@/context/AniContext";
import { AniPersonalityModePicker } from "./AniPersonalityModePicker";

interface ChatInputProps {
  recipientId: string;
  recipientName: string;
  conversationId?: string;
  conversationTitle?: string;
  /** When set, uploads are stored against this agentic v2 task (Mission Control thread). */
  workflowTaskId?: string;
  /** Personal-worker chat on agentic v2 (``conversation_documents`` uploads). */
  useAgenticV2Documents?: boolean;
  onSendMessage: (
    content: string,
    attachments: MessageAttachment[],
  ) => Promise<void>;
  disabled?: boolean;
  /** Overrides the empty-composer hint (e.g. while the agent is still working). */
  placeholder?: string;
  disableSpeechRecognition?: boolean;
  miniUpload?: boolean;
  // New prop for creating conversation before upload
  onCreateConversation?: () => Promise<string>;
  // Custom className to override input container background
  inputContainerClassName?: string;
  /** When set with {@link onAniPersonalityModeChange}, shows Ani mode control in the composer bar. */
  aniPersonalityMode?: AniPersonalityMode;
  onAniPersonalityModeChange?: (mode: AniPersonalityMode) => void;
  /** Prefill the composer (e.g. after navigating from Agent Review fixes). */
  initialDraft?: string;
  /** Dismissible context chip rendered inside the composer (not slash-command text). */
  composerChip?: { id: string; label: string } | null;
  onComposerChipDismiss?: () => void;
}

interface AttachedDocument extends DocumentsDocument {
  /** UI-only: pending upload / local file handle. */
  isPending?: boolean;
  file?: File; // Store the original file for deferred upload
  v2AttachmentType?: MessageAttachment["type"];
}

export function ChatInput({
  recipientId,
  recipientName,
  conversationId,
  conversationTitle,
  workflowTaskId,
  useAgenticV2Documents = false,
  onSendMessage,
  disabled = false,
  placeholder = "Enter message...",
  disableSpeechRecognition = false,
  miniUpload: _miniUpload = false,
  onCreateConversation,
  inputContainerClassName,
  aniPersonalityMode,
  onAniPersonalityModeChange,
  initialDraft,
  composerChip,
  onComposerChipDismiss,
}: ChatInputProps) {
  const { user } = useAuth();
  const [inputContent, setInputContent] = useState("");
  const appliedDraftRef = useRef(false);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [attachedDocuments, setAttachedDocuments] = useState<
    AttachedDocument[]
  >([]);
  const [isUploading, setIsUploading] = useState(false);
  const inputRef = useRef<HTMLDivElement>(null);

  const getInputPlainText = () => {
    const inputElement = inputRef.current;
    if (!inputElement) return "";

    return serializeContentEditableToPlainText(inputElement);
  };

  const { isListening, toggleVoiceInput } = useComposerVoiceInput({
    getBaseText: getInputPlainText,
    onTranscriptChange: (text) => {
      if (!inputRef.current) return;
      inputRef.current.textContent = text;
      setInputContent(text);
      inputRef.current.focus();
    },
    disabled,
  });

  useEffect(() => {
    const draft = initialDraft?.trim();
    if (!draft || appliedDraftRef.current || !inputRef.current) return;
    appliedDraftRef.current = true;
    inputRef.current.textContent = draft;
    setInputContent(draft);
    inputRef.current.focus();
  }, [initialDraft]);

  useEffect(() => {
    if (!composerChip) return;
    inputRef.current?.focus();
  }, [composerChip]);

  const clearInput = () => {
    if (inputRef.current) {
      inputRef.current.innerHTML = "";
      // Ensure placeholder shows up again
      inputRef.current.textContent = "";
      // Also clear the state
      setInputContent("");
    }
  };

  const handleUpload = () => {
    setIsUploadDialogOpen(true);
  };

  const handleCloseUploadDialog = () => {
    setIsUploadDialogOpen(false);
  };

  const handleUploadComplete = (
    result: DocumentsDocument | IngestAPIResponse,
  ) => {
    if ("document_id" in result) {
      const attached: AttachedDocument = { ...result };
      setAttachedDocuments((prev) => [...prev, attached]);
    }
    handleCloseUploadDialog();
  };

  const handleUploadError = (error: Error) => {
    console.error("Upload error:", error);
  };

  // New function to handle file selection without immediate upload
  const addPendingFile = (file: File) => {
    const pendingDoc: AttachedDocument = {
      document_id: `pending-${Date.now()}-${Math.random()}`,
      filename: file.name,
      file_type: file.type,
      upload_date: new Date().toISOString(),
      user_id: user?.id || "current-user",
      resource_type: conversationId ? "conversation" : "pod_instance",
      resource_id: conversationId || recipientId,
      metadata: { description: `Pending upload: ${file.name}` },
      chunk_count: 0,
      as_at: new Date().toISOString(),
      landscape: false,
      display_name: file.name,
      original_filename: file.name,
      size: file.size,
      mime_type: file.type,
      uploaded_by: {
        id: user?.id || "current-user",
        name: user?.fullname || "Current User",
      },
      isPending: true,
      file: file,
    };
    setAttachedDocuments((prev) => [...prev, pendingDoc]);
  };

  const handleFilesSelected = (files: File[]) => {
    for (const file of files) {
      addPendingFile(file);
    }
  };

  const removeAttachment = (documentId: string) => {
    setAttachedDocuments((prev) =>
      prev.filter((doc) => doc.document_id !== documentId),
    );
  };

  const uploadToInstance = async (file: File): Promise<AttachedDocument> => {
    // If we have a placeholder conversation ID, we need to create the conversation first
    let actualConversationId = conversationId;

    if (conversationId === "new-chat-placeholder") {
      if (!onCreateConversation) {
        throw new Error(
          "Cannot upload documents before conversation is created. Please send a message first.",
        );
      }
      actualConversationId = await onCreateConversation();
    }

    if (workflowTaskId) {
      const uploaded = await uploadTaskDocument(workflowTaskId, file);
      return {
        document_id: uploaded.document_id,
        filename: uploaded.filename,
        file_type: uploaded.mime_type,
        upload_date: new Date().toISOString(),
        user_id: user?.id || "current-user",
        resource_type: "task",
        resource_id: workflowTaskId,
        metadata: {},
        chunk_count: 0,
        as_at: new Date().toISOString(),
        landscape: false,
        display_name: uploaded.filename,
        original_filename: uploaded.filename,
        size: uploaded.size_bytes,
        mime_type: uploaded.mime_type,
        uploaded_by: {
          id: user?.id || "current-user",
          name: user?.fullname || "Current User",
        },
        v2AttachmentType: uploaded.attachment_type,
      };
    }

    if (useAgenticV2Documents && actualConversationId) {
      const uploaded = await uploadConversationDocument(
        actualConversationId,
        file,
      );
      return {
        document_id: uploaded.document_id,
        filename: uploaded.filename,
        file_type: uploaded.mime_type,
        upload_date: new Date().toISOString(),
        user_id: user?.id || "current-user",
        resource_type: "conversation",
        resource_id: actualConversationId,
        metadata: {},
        chunk_count: 0,
        as_at: new Date().toISOString(),
        landscape: false,
        display_name: uploaded.filename,
        original_filename: uploaded.filename,
        size: uploaded.size_bytes,
        mime_type: uploaded.mime_type,
        uploaded_by: {
          id: user?.id || "current-user",
          name: user?.fullname || "Current User",
        },
        v2AttachmentType: uploaded.attachment_type,
      };
    }

    // Legacy pods upload
    const documentAttributes = actualConversationId
      ? {
          conversation_id: actualConversationId,
          display_name: file.name,
          is_output: false,
          source_files: [],
          metadata: { description: `Uploaded document: ${file.name}` },
          uploaded_by: {
            id: "current-user",
            type: "user",
            name: "Current User",
          },
        }
        : {
            pod_instance_id: recipientId,
            workflow_task_id: "upload_task",
            display_name: file.name,
            is_output: false,
            source_files: [],
            metadata: { description: `Uploaded document: ${file.name}` },
            uploaded_by: {
              id: user?.id || "current-user",
              type: "user",
              name: user?.fullname || "Current User",
            },
          };

    const agenticDoc = await uploadAgenticDocument(file, documentAttributes);

    // Convert AgenticDocument to DocumentsDocument format
    return {
      document_id: agenticDoc._id,
      filename: agenticDoc.original_filename,
      file_type: agenticDoc.mime_type,
      upload_date: agenticDoc.uploaded_at,
      user_id: agenticDoc.uploaded_by?.id || "current-user",
      resource_type: actualConversationId ? "conversation" : "pod_instance",
      resource_id: actualConversationId || agenticDoc.pod_instance_id,
      metadata: agenticDoc.metadata,
      chunk_count: 0,
      as_at: agenticDoc.created_at,
      landscape: false,
      display_name: agenticDoc.display_name,
      original_filename: agenticDoc.original_filename,
      size: agenticDoc.size,
      mime_type: agenticDoc.mime_type,
      uploaded_by: {
        id: agenticDoc.uploaded_by?.id || "current-user",
        name: agenticDoc.uploaded_by?.name || "Current User",
      },
    };
  };

  const handleSendMessage = async () => {
    const content = getInputPlainText().trim();
    if ((!content && !composerChip) || disabled) return;

    try {
      setIsUploading(true);

      // Clear input and focus
      clearInput();

      // Separate pending and already uploaded documents
      const pendingDocs = attachedDocuments.filter(
        (doc) => doc.isPending && doc.file,
      );
      const uploadedDocs = attachedDocuments.filter((doc) => !doc.isPending);

      // Upload pending documents first
      const uploadedAttachments: MessageAttachment[] = [];
      let pendingUploadFailed = false;

      for (const pendingDoc of pendingDocs) {
        if (pendingDoc.file) {
          try {
            const uploadedDoc = await uploadToInstance(pendingDoc.file);
            uploadedAttachments.push({
              type: uploadedDoc.v2AttachmentType ?? "document",
              document_id: uploadedDoc.document_id,
              display_name: uploadedDoc.display_name,
              mime_type: uploadedDoc.mime_type,
              size: uploadedDoc.size,
            });
          } catch (error) {
            pendingUploadFailed = true;
            console.error(
              "Failed to upload document:",
              pendingDoc.display_name,
              error,
            );
            setAttachedDocuments((prev) =>
              prev.filter((doc) => doc.document_id !== pendingDoc.document_id),
            );
          }
        }
      }

      if (pendingDocs.length > 0 && pendingUploadFailed) {
        console.error(
          "Message not sent: fix document upload errors before sending.",
        );
        return;
      }

      // Add already uploaded documents
      uploadedAttachments.push(
        ...uploadedDocs.map((doc) => ({
          type: doc.v2AttachmentType ?? ("document" as const),
          document_id: doc.document_id,
          display_name: doc.display_name,
          mime_type: doc.mime_type,
          size: doc.size,
        })),
      );

      await onSendMessage(content, uploadedAttachments);

      // Clear all attachments
      setAttachedDocuments([]);

      // Focus back on input
      inputRef.current?.focus();
    } catch (error) {
      console.error("Error in ChatInput:", error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleKeyPress = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleInputChange = () => {
    if (inputRef.current) {
      setInputContent(getInputPlainText());
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text/plain");
    if (!inputRef.current || !text) return;
    insertPlainTextAtSelection(text, inputRef.current);
    handleInputChange();
  };

  return (
    <div className="p-4">
      <div className="flex-1 min-w-0">
        {/* Attachments Preview */}
        {attachedDocuments.length > 0 && (
          <div className="flex flex-wrap gap-2 z-0   relative mb-2   p-2 bg-slate-100 dark:bg-slate-800 rounded-lg">
            {attachedDocuments.map((doc) => (
              <div
                key={doc.document_id}
                className="flex items-center gap-2 rounded-xl bg-white px-3 pr-0 text-sm text-slate-900 transition-all dark:bg-slate-700 dark:text-white"
              >
                <FontAwesomeIcon icon={faFile} />
                <span className="max-w-[150px] truncate">
                  {doc.display_name}
                </span>
                <Button
                  onClick={() => removeAttachment(doc.document_id)}
                  aria-label={`Remove ${doc.display_name}`}
                  plain
                >
                  <FontAwesomeIcon icon={faXmark} />
                </Button>
              </div>
            ))}
          </div>
        )}

        <div className={`relative rounded-2xl focus-within:ring-2 focus-within:ring-indigo-400 focus-within:ring-offset-2 focus-within:ring-offset-white dark:focus-within:ring-offset-slate-700 ${inputContainerClassName || "border border-slate-200 bg-slate-100 dark:border-transparent dark:bg-slate-800/90"}`}>
          <div className="flex min-h-[80px] max-h-[200px] flex-col overflow-y-auto px-3 pt-2">
            {composerChip ? (
              <div className="mb-1.5">
                <Badge
                  variant="chip"
                  color="indigo"
                  size="sm"
                  onDismiss={onComposerChipDismiss}
                  data-testid="chat-input-composer-chip"
                >
                  {composerChip.label}
                </Badge>
              </div>
            ) : null}
            <div
              ref={inputRef}
              contentEditable={!disabled}
              onKeyDown={handleKeyPress}
              onInput={handleInputChange}
              onPaste={handlePaste}
              className={`w-full min-h-[48px] flex-1 cursor-text whitespace-pre-wrap bg-transparent text-slate-900 focus:outline-none dark:text-white ${
                styles.contentEditable
              } ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
              data-placeholder={placeholder}
              role="textbox"
              aria-multiline="true"
              data-lpignore="true"
              data-form-type="other"
            />
          </div>

          {/* Toolbar: Ani mode + title (left), newline hint + actions (right) */}
          <div className="flex w-full items-center gap-2 px-3 py-2 text-slate-500 dark:text-slate-400">
            <div className="flex min-w-0 flex-1 items-center gap-2">
              {aniPersonalityMode !== undefined && onAniPersonalityModeChange && (
                <div className="shrink-0">
                  <AniPersonalityModePicker
                    value={aniPersonalityMode}
                    onChange={onAniPersonalityModeChange}
                    disabled={disabled}
                  />
                </div>
              )}
              {conversationTitle && (
                <div className="min-w-0 flex-1 truncate text-xs font-mono text-slate-500 dark:text-slate-400">
                  {conversationTitle}
                </div>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <div
                className="shrink-0 text-xs font-mono text-slate-500 dark:text-slate-400 whitespace-nowrap"
                data-testid="chat-input-newline-help"
              >
                Shift+Enter for new lines
              </div>
              <Button plain onClick={handleUpload} disabled={disabled}>
              <FontAwesomeIcon
                className="text-slate-600 dark:text-slate-400"
                icon={faPaperclip}
              />
              {/* {!miniUpload && (
                <span className="text-sm font-mono text-slate-500 dark:text-slate-400 font-normal">
                  Upload document
                </span>
              )} */}
                <span className="sr-only">Upload</span>
              </Button>
              {!disableSpeechRecognition && (
                <Button
                  plain
                  onClick={() => void toggleVoiceInput()}
                  disabled={disabled}
                  aria-pressed={isListening}
                  aria-label={isListening ? "Stop voice input" : "Start voice input"}
                >
                  <FontAwesomeIcon
                    icon={isListening ? faCircle : faMicrophone}
                    className={
                      isListening
                        ? "text-red-500 animate-pulse"
                        : "text-slate-600 dark:text-slate-400"
                    }
                  />
                </Button>
              )}

              <Button
                plain
                onClick={handleSendMessage}
                disabled={(!inputContent.trim() && !composerChip) || disabled || isUploading}
              >
                <FontAwesomeIcon
                  icon={faPaperPlaneTop}
                  className="text-slate-600 dark:text-slate-400"
                />
                <span className="sr-only">Send</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <UploadDialog
        isOpen={isUploadDialogOpen}
        onClose={handleCloseUploadDialog}
        title="Upload Document"
        description={`Attach one or more files for ${recipientName} (uploaded when you send)`}
        uploadFunction={uploadToInstance}
        onUploadComplete={handleUploadComplete}
        onUploadError={handleUploadError}
        onFilesSelected={handleFilesSelected}
        multiple
      />
    </div>
  );
}
